import { useState, type FormEvent } from 'react';
import { supabase } from '../sync/supabase';
import './SignIn.css';

/**
 * Seen once per device, then never again.
 *
 * The brief puts account management out of scope — there is one user,
 * permanently signed in — so this is deliberately not a product surface: no
 * sign-up, no password reset, no branding. Just the one door, opened once.
 */
export function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!supabase || busy) return;
    setBusy(true);
    setError(null);

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setBusy(false);
  }

  return (
    <div className="signin">
      <form className="signin__form" onSubmit={submit}>
        <div className="signin__head">
          <span className="signin__brand">SlippingNotes</span>
          <span className="signin__hint">Sign in once. This device stays signed in.</span>
        </div>

        <label className="signin__field">
          <span className="signin__label">Email</span>
          <input
            className="signin__input"
            type="email"
            value={email}
            autoFocus
            autoComplete="username"
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="signin__field">
          <span className="signin__label">Password</span>
          <input
            className="signin__input"
            type="password"
            value={password}
            autoComplete="current-password"
            required
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && <div className="signin__error">{error}</div>}

        <button className="signin__submit" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="signin__note">
          Everything works offline once you are in. Nothing is written to the server that is not
          already on this device.
        </p>
      </form>
    </div>
  );
}
